import React, { useEffect, useRef } from 'react';
import { Avatar } from './Avatar';
import { areaMeta } from './taskModel';

// Portal Work (v5) — the mobile "Sort & filter" bottom sheet, the single surface
// behind the header's sliders button under 768px. It edits the module's real
// filter/sort state live (no draft copy), so the "Show N tasks" button reflects
// the current match count and simply dismisses — behaviour + semantics are
// unchanged from the shipped chip bar; this is the touch packaging of it.
//
// a11y bar (the reason the native pieces were deferred): role="dialog" +
// aria-modal, a focus trap, Esc + scrim dismiss, body scroll lock, and focus
// returned to whatever opened it. Sort is a native radiogroup (arrow-key nav for
// free); area / assignee are aria-pressed toggles; parked is a role="switch".

const FOCUSABLE =
	'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';

// NOTE ON `showCompleted`: it is a persisted per-user DISPLAY preference, not a
// filter, which is why it is absent from this sheet's clear behaviour. Kept out
// of the destructure block as a comment because SortFilterSheet.test.js scrapes
// that block for prop names to check every one is passed at the call site — a
// comment inside it reads as a prop and fails the pin.
function SortFilterSheet({
	sortBy,
	setSortBy,
	sortOptions,
	areasInUse,
	areaFilter,
	toggleArea,
	peopleInUse,
	assigneeFilter,
	toggleAssignee,
	parkedOnly,
	setParkedOnly,
	needsTriageOnly,
	setNeedsTriageOnly,
	needsInfoOnly,
	setNeedsInfoOnly,
	mailOnly,
	setMailOnly,
	showCompleted,
	setShowCompleted,
	showCount,
	onClose,
}) {
	const panelRef = useRef(null);
	const openerRef = useRef(null);

	// Capture the opener so focus returns there on close; lock body scroll while
	// the sheet is up. Move initial focus into the panel (its close button).
	useEffect(() => {
		openerRef.current =
			document.activeElement instanceof HTMLElement ? document.activeElement : null;
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		const raf = requestAnimationFrame(() => {
			const first = panelRef.current?.querySelector('.wt-sheet__close');
			if (first) first.focus();
		});
		return () => {
			cancelAnimationFrame(raf);
			document.body.style.overflow = prevOverflow;
			if (openerRef.current) openerRef.current.focus();
		};
	}, []);

	const onKeyDown = (e) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			onClose();
			return;
		}
		if (e.key !== 'Tab') return;
		// Focus trap: wrap Tab / Shift+Tab within the panel.
		const nodes = panelRef.current
			? Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter(
					(n) => n.offsetParent !== null
			  )
			: [];
		if (!nodes.length) return;
		const first = nodes[0];
		const last = nodes[nodes.length - 1];
		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	};

	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<div className="wt-sheet-overlay" onKeyDown={onKeyDown}>
			{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
			<div className="wt-sheet__scrim" onClick={onClose} />
			<div
				ref={panelRef}
				className="wt-sheet"
				role="dialog"
				aria-modal="true"
				aria-labelledby="wt-sheet-title">
				<span className="wt-sheet__grab" aria-hidden="true" />
				<div className="wt-sheet__head">
					<h2 className="wt-sheet__title" id="wt-sheet-title">
						Sort &amp; filter
					</h2>
					<button
						type="button"
						className="wt-sheet__close"
						aria-label="Close sort and filter"
						onClick={onClose}>
						<i className="fa-sharp fa-solid fa-xmark" />
					</button>
				</div>

				<div className="wt-sheet__scroll">
					<fieldset className="wt-sheet__group">
						<legend className="wt-sheet__eyebrow">Sort by</legend>
						{sortOptions.map((o) => (
							<label key={o.key} className="wt-sheet__radio">
								<input
									type="radio"
									name="wt-sheet-sort"
									value={o.key}
									checked={sortBy === o.key}
									onChange={() => setSortBy(o.key)}
								/>
								<span className="wt-sheet__radiomark" aria-hidden="true" />
								<span className="wt-sheet__radiolabel">{o.label}</span>
							</label>
						))}
						<p className="wt-sheet__hint">Tasks without a value sort last</p>
					</fieldset>

					{areasInUse.length > 0 && (
						<div className="wt-sheet__group">
							<div className="wt-sheet__eyebrow">Area</div>
							<div className="wt-sheet__chips">
								{areasInUse.map((a) => {
									const on = areaFilter.has(a);
									return (
										<button
											key={a}
											type="button"
											className={`wt-sheet__chip${on ? ' is-active' : ''}`}
											aria-pressed={on}
											onClick={() => toggleArea(a)}>
											<span
												className="wt-tag__dot"
												style={{ '--row-accent': `var(--area-${a})` }}
											/>
											{areaMeta(a).label}
										</button>
									);
								})}
							</div>
						</div>
					)}

					{peopleInUse.length > 0 && (
						<div className="wt-sheet__group">
							<div className="wt-sheet__eyebrow">Assignee</div>
							<div className="wt-sheet__chips">
								{peopleInUse.map((p) => {
									const on = assigneeFilter.has(p.uid);
									return (
										<button
											key={p.uid}
											type="button"
											className={`wt-sheet__chip wt-sheet__chip--who${
												on ? ' is-active' : ''
											}`}
											aria-pressed={on}
											onClick={() => toggleAssignee(p.uid)}>
											<Avatar uid={p.uid} size={22} name={p.name} />
											{p.name}
										</button>
									);
								})}
							</div>
						</div>
					)}

					<button
						type="button"
						className="wt-sheet__switchrow"
						role="switch"
						aria-checked={parkedOnly}
						onClick={() => setParkedOnly((v) => !v)}>
						<span className="wt-sheet__switchlabel">
							<i className="fa-sharp fa-solid fa-circle-pause" />
							Only parked tasks
						</span>
						<span
							className={`wt-sheet__switch${parkedOnly ? ' is-on' : ''}`}
							aria-hidden="true">
							<span className="wt-sheet__switchknob" />
						</span>
					</button>

					{/* The two triage flags. These exist as chips in the desktop filter
					    panel, but that panel is unreachable under 768px (the sliders
					    button opens THIS sheet instead of toggling it), so without
					    these rows the phone had no path to either filter at all. */}
					<button
						type="button"
						className="wt-sheet__switchrow"
						role="switch"
						aria-checked={needsTriageOnly}
						onClick={() => setNeedsTriageOnly((v) => !v)}>
						<span className="wt-sheet__switchlabel">
							<i className="fa-sharp fa-solid fa-inbox" />
							Only needs triage
						</span>
						<span
							className={`wt-sheet__switch${needsTriageOnly ? ' is-on' : ''}`}
							aria-hidden="true">
							<span className="wt-sheet__switchknob" />
						</span>
					</button>

					<button
						type="button"
						className="wt-sheet__switchrow"
						role="switch"
						aria-checked={needsInfoOnly}
						onClick={() => setNeedsInfoOnly((v) => !v)}>
						<span className="wt-sheet__switchlabel">
							<i className="fa-sharp fa-solid fa-circle-question" />
							Only needs info
						</span>
						<span
							className={`wt-sheet__switch${needsInfoOnly ? ' is-on' : ''}`}
							aria-hidden="true">
							<span className="wt-sheet__switchknob" />
						</span>
					</button>

					<button
						type="button"
						className="wt-sheet__switchrow"
						role="switch"
						aria-checked={mailOnly}
						onClick={() => setMailOnly((v) => !v)}>
						<span className="wt-sheet__switchlabel">
							<i className="fa-sharp fa-solid fa-envelope" />
							Only mail
						</span>
						<span
							className={`wt-sheet__switch${mailOnly ? ' is-on' : ''}`}
							aria-hidden="true">
							<span className="wt-sheet__switchknob" />
						</span>
					</button>

					<button
						type="button"
						className="wt-sheet__switchrow"
						role="switch"
						aria-checked={showCompleted}
						onClick={() => setShowCompleted(!showCompleted)}>
						<span className="wt-sheet__switchlabel">
							<i className={`fa-sharp fa-solid ${showCompleted ? 'fa-eye' : 'fa-eye-slash'}`} />
							Show completed
						</span>
						<span
							className={`wt-sheet__switch${showCompleted ? ' is-on' : ''}`}
							aria-hidden="true">
							<span className="wt-sheet__switchknob" />
						</span>
					</button>
				</div>

				<button
					type="button"
					className="btn-cyv-teal wt-sheet__apply"
					onClick={onClose}>
					Show {showCount} task{showCount === 1 ? '' : 's'}
				</button>
			</div>
		</div>
	);
}

export default SortFilterSheet;
